/* eslint-disable max-len */
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faCalendar } from '@fortawesome/free-solid-svg-icons/faCalendar';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, filter, Observable, skip, Subscription, switchMap, take } from 'rxjs';
import { getLimitOfMaxTimes } from 'src/app/helpers/reminder/reminder';
import { InterventionInstanceReminderLogsInterface } from 'src/app/models/interface/intervention-instance-reminder-logs.interface';
import { HelperService } from 'src/app/services/helper/helper.service';
import { ReminderStore } from 'src/app/store/reminder/component-store/reminder.store';
import { InterventionInstanceReminderInterface } from '../../../models/interface/intervention-instance-reminder.interface';

type InstanceReminderData = {
  studyId: number;
  reminder: InterventionInstanceReminderInterface;
};

@Component({
  selector: 'app-dialog-intervention-instance-reminder-detail',
  templateUrl: './dialog-intervention-instance-reminder-detail.component.html',
  styleUrls: ['./dialog-intervention-instance-reminder-detail.component.scss'],
  providers: [ReminderStore],
})
export class DialogInterventionInstanceReminderDetailComponent implements OnInit {
  // Icons
  faBell = faBell;
  faCalendar = faCalendar;

  public studyId: number;

  public selectedLanguageSubject = new BehaviorSubject<string>('');

  public translations: object;

  // Reminder form submission
  public reminderForm: UntypedFormGroup;

  public updateInterventionInstanceReminderResponse = new BehaviorSubject<string>('DEFAULT');
  public deleteInterventionInstanceReminderResponse = new BehaviorSubject<string>('DEFAULT');

  public displaySubject = new BehaviorSubject<'DEFAULT' | 'UPDATE' | 'DELETE'>('DEFAULT');

  public updateInterventionInstanceReminderResponse$: Observable<any>;
  public updateInterventionInstanceReminder$: Observable<any>;
  public deleteInterventionInstanceReminderResponse$: Observable<any>;
  public deleteInterventionInstanceReminder$: Observable<any>;

  public allReminderLogsOfStudy$: Observable<InterventionInstanceReminderLogsInterface[]>;

  public reminderLogs$: Observable<InterventionInstanceReminderLogsInterface[]>;
  public reminderLogsSubject = new BehaviorSubject<InterventionInstanceReminderLogsInterface[]>([]);
  public pagedReminderLogSubject = new BehaviorSubject<InterventionInstanceReminderLogsInterface[]>([]);
  public pagedReminderLog$: Observable<InterventionInstanceReminderLogsInterface[]> = this.pagedReminderLogSubject.asObservable();

  public unitList = ['minute', 'hour', 'day'] as const;

  // Ngx datetimepicker
  public showSpinners = false;
  public showSeconds = false;
  public touchUi = false;
  public enableMeridian = false;
  public minDate = new Date();
  public stepHour = 1;
  public stepMinute = 1;
  public stepSecond = 1;
  public color: ThemePalette = 'primary';

  public reminder: InterventionInstanceReminderInterface;

  public isLoading$: Observable<boolean>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  maxTimesErrorMessage = undefined;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InstanceReminderData,
    private dialogRef: MatDialogRef<DialogInterventionInstanceReminderDetailComponent>,
    private helperService: HelperService,
    private reminderStore: ReminderStore,
    private httpClient: HttpClient,
    private translateService: TranslateService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.selectedLanguageSubject.next(this.translateService.currentLang);
    this.subscriptions.push(
      this.selectedLanguageSubject
        .pipe(
          distinctUntilChanged(),
          switchMap((language) => this.httpClient.get(`../../../../assets/i18n/${language}.json`))
        )
        .subscribe((translations) => {
          this.translations = translations;
        }),
    );
    this.reminderForm = new UntypedFormGroup({
      starts_at: new UntypedFormControl('', [Validators.required]),
      max_times: new UntypedFormControl('', [Validators.required]),
      time_interval: new UntypedFormControl('', [Validators.required]),
      time_interval_unit: new UntypedFormControl(this.unitList[0]),
      content_greeting: new UntypedFormControl(''),
      content_text: new UntypedFormControl(''),
      content_closing: new UntypedFormControl(''),
      content_signature: new UntypedFormControl(''),
    });
    this.reminderForm.controls['starts_at'].setValue(new Date());
    this.reminderLogs$ = this.reminderLogsSubject.asObservable();

    this.updateInterventionInstanceReminderResponse$ = this.reminderStore.updateInterventionInstanceReminderResponse$;
    this.deleteInterventionInstanceReminderResponse$ = this.reminderStore.deleteInterventionInstanceReminderResponse$;
    this.allReminderLogsOfStudy$ = this.reminderStore.allReminderLogsOfStudy$;
    this.updateInterventionInstanceReminder$ = this.updateInterventionInstanceReminderResponse.asObservable();
    this.deleteInterventionInstanceReminder$ = this.deleteInterventionInstanceReminderResponse.asObservable();
  }

  public get f() {
    return this.reminderForm;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.studyId = this.data.studyId;
    this.reminder = this.data.reminder;
    if (this.studyId) {
      const instanceId = this.reminder?.attributes?.model_id?.toString();
      this.reminderStore.getAllReminderLogsOfStudy({
        studyId: this.studyId,
        interventionInstanceId: instanceId,
        // Only get logs of user as fallback in case instanceId is missing
        userId: instanceId == null ? this.reminder?.attributes?.receiver_id : undefined,
      });
      this.subscriptions.push(
        this.allReminderLogsOfStudy$.pipe(skip(1), take(1)).subscribe((reminderLogs) => {
          const descendingInstanceReminderLogs = reminderLogs
            .filter((log) => log.attributes.intervention_instance_id.toString() === instanceId)
            .sort((a, b) => b.attributes.created_at - a.attributes.created_at);
          this.reminderLogsSubject.next(descendingInstanceReminderLogs);
          this.pagedReminderLogSubject.next(this.reminderLogsSubject.value.slice(0, 20));
          this.isLoadingSubject.next(false);
        }),
      );
    }
    if (this.reminder) {
      const reminderConfig = this.reminder.attributes;
      this.f.get('starts_at').patchValue(new Date(this.helper.convertUnixTimestampToDate(reminderConfig.start)));
      this.f.get('max_times').patchValue(reminderConfig.maximum_reminders || '');
      this.f.get('time_interval').patchValue(reminderConfig.configuration.pause || '');
      this.f.get('time_interval_unit').patchValue(reminderConfig.configuration.unit || '');
      this.f.get('content_greeting').patchValue(reminderConfig.content?.greeting || '');
      this.f.get('content_text').patchValue(reminderConfig.content?.text || '');
      this.f.get('content_closing').patchValue(reminderConfig.content?.closing || '');
      this.f.get('content_signature').patchValue(reminderConfig.content?.signature || '');
    }
  }

  public getReminderGreeting(): string {
    if (this.translations) {
      return this.translations['intervention-instance-reminder']['default_reminder_message_head'];
    } else {
      return '';
    }
  }

  public getReminderMessage(): string {
    if (this.translations) {
      return this.translations['intervention-instance-reminder']['default_reminder_message'];
    } else {
      return '';
    }
  }

  public getReminderClosing(): string {
    if (this.translations) {
      return this.translations['intervention-instance-reminder']['default_reminder_closing'];
    } else {
      return '';
    }
  }

  public getReminderSignature(): string {
    if (this.translations) {
      return this.translations['intervention-instance-reminder']['default_reminder_signature'];
    } else {
      return '';
    }
  }

  public setDisplay(value: 'DEFAULT' | 'UPDATE' | 'DELETE'): void {
    this.displaySubject.next(value);
  }

  getLimitOfMaxTimesOf(unit: 'minute' | 'hour' | 'day') {
    return getLimitOfMaxTimes(unit);
  }

  controlHasErrors(control: AbstractControl): boolean {
    return control.touched && control.invalid;
  }

  resetMaxTimesErrorMessage() {
    this.maxTimesErrorMessage = undefined;
  }

  getValue(key: string): any | undefined {
    const value = this.f.get(key).value;
    return value || value === '' ? value : undefined;
  }

  public updateInterventionInstanceReminder(): void {
    if (this.reminderForm.invalid) {
      return;
    }

    const startsAt = this.helperService.convertStringToUnixTimestamp(this.getValue('starts_at'));
    const maxTimes = this.getValue('max_times');
    const timeInterval = this.getValue('time_interval');
    const timeIntervalUnit = this.getValue('time_interval_unit');
    const contentGreeting = this.getValue('content_greeting') || this.getReminderGreeting();
    const contentText = this.getValue('content_text') || this.getReminderMessage();
    const contentClosing = this.getValue('content_closing') || this.getReminderClosing();
    const contentSignature = this.getValue('content_signature') || this.getReminderSignature();

    if (this.updateInterventionInstanceReminderResponse.value === 'DEFAULT') {
      this.updateInterventionInstanceReminderResponse.next('LOADING');

      this.reminderStore.updateInterventionInstanceReminder({
        reminderId: this.reminder.id,
        receiverId: this.reminder.attributes.receiver_id,
        maxTimes,
        start: startsAt,
        repeatTime: timeInterval,
        repeatUnit: timeIntervalUnit,
        contentGreeting,
        contentText,
        contentClosing,
        contentSignature,
        locale: this.translateService.currentLang,
      });

      this.subscriptions.push(
        this.updateInterventionInstanceReminderResponse$
          .pipe(
            filter((value) => !!value),
            take(1),
          )
          .subscribe((value) => {
            if (value instanceof HttpResponse) {
              this.updateInterventionInstanceReminderResponse.next('SUCCESS');
              setTimeout(() => {
                this.updateInterventionInstanceReminderResponse.next('DEFAULT');
                this.dialogRef.close(value);
              }, 2500);
            } else {
              if (value.status === 422) {
                const errors = value.error?.errors[0]?.meta?.errors;
                const maxTimesKey = "data.attributes.configuration.max_times";
                if (errors && maxTimesKey in errors) {
                  const maxTimesErrors = errors[maxTimesKey];
                  const defaultError = this.translateService.instant(
                    'intervention-instance-reminder.invalid_max_number_of_repititions',
                  );
                  if (Array.isArray(maxTimesErrors)) {
                    this.maxTimesErrorMessage = maxTimesErrors[0]?.message[0] || defaultError;
                  } else {
                    this.maxTimesErrorMessage = maxTimesErrors || defaultError;
                  }
                }
              }
              this.updateInterventionInstanceReminderResponse.next('FAILURE');
              setTimeout(() => {
                this.updateInterventionInstanceReminderResponse.next('DEFAULT');
              }, 2500);
            }
          })
      );
    }
  }

  public deleteInterventionInstanceReminder(): void {
    if (this.deleteInterventionInstanceReminderResponse.value === 'DEFAULT') {
      this.deleteInterventionInstanceReminderResponse.next('LOADING');

      this.reminderStore.deleteInterventionInstanceReminder({
        reminderId: this.reminder.id
      });

      this.subscriptions.push(
        this.deleteInterventionInstanceReminderResponse$
          .pipe(
            filter(value => !!value),
            take(1)
          )
          .subscribe((value: any) => {
            if (value instanceof HttpResponse) {
              this.deleteInterventionInstanceReminderResponse.next('SUCCESS');
              setTimeout(() => {
                this.deleteInterventionInstanceReminderResponse.next('DEFAULT');
                this.dialogRef.close(value);
              }, 2500);
            } else {
              this.deleteInterventionInstanceReminderResponse.next('FAILURE');
              setTimeout(() => {
                this.deleteInterventionInstanceReminderResponse.next('DEFAULT');
              }, 2500);
            }
          })
      );
    }
  }

  public isDisabled(): boolean {
    return (
      this.deleteInterventionInstanceReminderResponse.value !== 'DEFAULT' ||
      this.updateInterventionInstanceReminderResponse.value !== 'DEFAULT'
    );
  }

  public updatePagedReminderLogs(event: any) {
    if (event) {
      this.pagedReminderLogSubject.next(event);
    }
  }
}
